import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import type { ProductWithImages } from '@dukanti/shared';
import { cn } from '@/lib/utils';

const MENU_MIN_WIDTH = 160;
const MENU_EDGE_GAP = 8;
const MENU_TRIGGER_GAP = 4;
const MENU_ESTIMATED_HEIGHT = 136;

interface MenuPosition {
  readonly top: number;
  readonly left: number;
  readonly minWidth: number;
}

export interface ProductActionsMenuProps {
  readonly product: ProductWithImages;
  readonly storePublished: boolean;
  readonly onRequestDelete?: (product: ProductWithImages) => void;
  readonly onRequestPublish?: (product: ProductWithImages) => void;
  readonly onRequestUnpublish?: (product: ProductWithImages) => void;
  readonly testIdPrefix: string;
  readonly triggerClassName?: string;
  readonly menuAlign?: 'start' | 'end';
}

export function ProductActionsMenu({
  product,
  storePublished,
  onRequestDelete,
  onRequestPublish,
  onRequestUnpublish,
  testIdPrefix,
  triggerClassName,
  menuAlign = 'end',
}: ProductActionsMenuProps) {
  const { t, dir } = useTranslation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const triggerRect = trigger.getBoundingClientRect();
    const menuWidth = Math.max(menuRef.current?.offsetWidth ?? MENU_MIN_WIDTH, MENU_MIN_WIDTH);
    const menuHeight = menuRef.current?.offsetHeight ?? MENU_ESTIMATED_HEIGHT;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const alignsToLogicalEnd = menuAlign === 'end';
    const inlineEndIsRight = dir !== 'rtl';
    const preferredLeft = alignsToLogicalEnd
      ? inlineEndIsRight
        ? triggerRect.right - menuWidth
        : triggerRect.left
      : inlineEndIsRight
        ? triggerRect.left
        : triggerRect.right - menuWidth;
    const maxLeft = Math.max(MENU_EDGE_GAP, viewportWidth - menuWidth - MENU_EDGE_GAP);
    const belowTop = triggerRect.bottom + MENU_TRIGGER_GAP;
    const aboveTop = triggerRect.top - menuHeight - MENU_TRIGGER_GAP;
    const fitsBelow = belowTop + menuHeight <= viewportHeight - MENU_EDGE_GAP;
    const fitsAbove = aboveTop >= MENU_EDGE_GAP;
    const preferredTop = fitsBelow || !fitsAbove ? belowTop : aboveTop;
    const maxTop = Math.max(MENU_EDGE_GAP, viewportHeight - menuHeight - MENU_EDGE_GAP);

    setMenuPosition({
      top: Math.max(MENU_EDGE_GAP, Math.min(preferredTop, maxTop)),
      left: Math.max(MENU_EDGE_GAP, Math.min(preferredLeft, maxLeft)),
      minWidth: Math.max(triggerRect.width, MENU_MIN_WIDTH),
    });
  }, [dir, menuAlign]);

  useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuPosition(null);
      return;
    }

    updateMenuPosition();
    const frameId = window.requestAnimationFrame(updateMenuPosition);
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [menuOpen, updateMenuPosition]);

  useEffect(() => {
    if (!menuOpen) return;

    function onDocClick(e: MouseEvent) {
      const target = e.target as Node;
      const clickedTrigger = rootRef.current?.contains(target) ?? false;
      const clickedMenu = menuRef.current?.contains(target) ?? false;

      if (!clickedTrigger && !clickedMenu) {
        setMenuOpen(false);
      }
    }

    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [menuOpen]);

  function stopEvent(e: React.MouseEvent) {
    e.stopPropagation();
  }

  function onKebabClick(e: React.MouseEvent) {
    stopEvent(e);
    setMenuOpen((prev) => !prev);
  }

  function onEditClick(e: React.MouseEvent) {
    stopEvent(e);
    setMenuOpen(false);
    navigate(`/products/${product.id}/edit`);
  }

  function onDeleteClick(e: React.MouseEvent) {
    stopEvent(e);
    setMenuOpen(false);
    onRequestDelete?.(product);
  }

  function onPublishClick(e: React.MouseEvent) {
    stopEvent(e);
    setMenuOpen(false);
    onRequestPublish?.(product);
  }

  function onUnpublishClick(e: React.MouseEvent) {
    stopEvent(e);
    setMenuOpen(false);
    onRequestUnpublish?.(product);
  }

  const publishDisabled = !storePublished || product.inventory <= 0;
  const menuStyle: CSSProperties = menuPosition
    ? {
        top: menuPosition.top,
        left: menuPosition.left,
        minWidth: menuPosition.minWidth,
      }
    : {
        top: 0,
        left: 0,
        minWidth: MENU_MIN_WIDTH,
        visibility: 'hidden',
      };
  const menu = menuOpen
    ? createPortal(
        <div
          ref={menuRef}
          role="menu"
          dir={dir}
          data-testid={`${testIdPrefix}-menu`}
          style={menuStyle}
          className={cn(
            'fixed z-50 overflow-hidden rounded-md',
            'border-border bg-popover text-popover-foreground shadow-admin-elevated border py-1',
          )}
        >
          {product.published ? (
            <button
              type="button"
              role="menuitem"
              onClick={onUnpublishClick}
              className="hover:bg-admin-surface-muted text-foreground flex w-full items-center gap-2 px-3 py-2 text-start text-sm"
              data-testid={`${testIdPrefix}-menu-unpublish`}
            >
              <EyeOff className="h-4 w-4" strokeWidth={1.75} />
              {t('admin.products.publish.card.menuUnpublish')}
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              onClick={onPublishClick}
              disabled={publishDisabled}
              aria-disabled={publishDisabled || undefined}
              className={cn(
                'hover:bg-admin-surface-muted text-foreground flex w-full items-center gap-2 px-3 py-2 text-start text-sm',
                publishDisabled && 'pointer-events-none opacity-50',
              )}
              data-testid={`${testIdPrefix}-menu-publish`}
            >
              <Eye className="h-4 w-4" strokeWidth={1.75} />
              {t('admin.products.publish.card.menuPublish')}
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={onEditClick}
            className="hover:bg-admin-surface-muted text-foreground flex w-full items-center gap-2 px-3 py-2 text-start text-sm"
            data-testid={`${testIdPrefix}-menu-edit`}
          >
            <Pencil className="h-4 w-4" strokeWidth={1.75} />
            {t('admin.products.card.editAction')}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={onDeleteClick}
            className="text-destructive hover:bg-destructive/10 flex w-full items-center gap-2 px-3 py-2 text-start text-sm"
            data-testid={`${testIdPrefix}-menu-delete`}
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.75} />
            {t('admin.products.card.deleteAction')}
          </button>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className="relative" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={onKebabClick}
        aria-label={t('admin.products.card.actionsMenu')}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        data-testid={`${testIdPrefix}-kebab`}
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded',
          'text-muted-foreground transition-[opacity,background-color,color]',
          'hover:bg-admin-surface-muted hover:text-foreground',
          'focus-visible:ring-zaatar focus-visible:outline-none focus-visible:ring-2',
          triggerClassName,
          menuOpen && 'opacity-100',
        )}
      >
        <MoreVertical className="h-4 w-4" strokeWidth={2} />
      </button>

      {menu}
    </div>
  );
}
